import {
  Divider,
  Flex,
  Heading,
  Icon,
  Image,
  PlainButton,
  Stack,
  TextLabel,
  VisuallyHidden,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { CATEGORY_ICONS } from '../constants';
import type { Video } from '../types';
import { Duration } from './duration';
import * as styles from './video-preview.css';

interface VideoPreviewProps {
  video: Video;
  direction?: 'row' | 'column';
  onSelect: () => void;
}

export function VideoPreview({ video, direction = 'column', onSelect }: VideoPreviewProps) {
  const iconName = CATEGORY_ICONS[video.category];
  const isDirectionRow = direction === 'row';
  const isValidDuration = !Number.isNaN(video.duration) && video.duration >= 0;
  const isDisabled = isEmptyString(video.thumbnailUrl) || !isValidDuration;

  return (
    <PlainButton
      onClick={onSelect}
      isDisabled={isDisabled}
      className={isDirectionRow ? styles.videoButtonRow : styles.videoButton}
      data-test-video-library-link
    >
      <Flex direction={direction} gap="100" h="full">
        <Flex
          justifyContent="flex-start"
          alignItems="flex-start"
          className={isDirectionRow ? styles.thumbnailRow : styles.thumbnail}
        >
          {!isEmptyString(video.thumbnailUrl) ? (
            <Image src={video.thumbnailUrl} alt="" aria-hidden="true" objectFit="cover" w="full" h="full" />
          ) : (
            <Flex alignItems="center" justifyContent="center" h="full" w="full">
              <Icon color="subtle" name="image" size="lg" />
            </Flex>
          )}
        </Flex>

        <Stack gap="100" flex={isDirectionRow ? undefined : 1} w="full">
          <VisuallyHidden>Click to play video:</VisuallyHidden>
          <Heading level="2" size="s" textAlign="left">
            {video.title}
          </Heading>

          {isValidDuration ? (
            <Flex
              justifyContent={isDirectionRow ? 'flex-start' : 'space-between'}
              alignItems="center"
              flexWrap="wrap"
              w="full"
            >
              <Flex alignItems="center" gap="025">
                <Icon color="subtle" size="sm" name={iconName} aria-hidden="true" />
                <TextLabel aria-hidden="true">{video.category}</TextLabel>
              </Flex>
              {isDirectionRow ? <Divider direction="vertical" mx="100" className={styles.dividerVertical} /> : null}
              <Duration seconds={video.duration} data-test-video-library-duration />
            </Flex>
          ) : null}
        </Stack>
      </Flex>
    </PlainButton>
  );
}
